import {skipToken} from '@reduxjs/toolkit/query';

import {useMemo} from 'react';

import {isEmpty, isNil, isNotNil} from 'ramda';

import {useFilter} from '../../../hooks/FilterContext';
import {PriceReportType} from '../../../types/PriceReportType';
import {toMetricsRequestBody} from '../utils/buildMetricsRequestBody';
import {useMarketMetrics, UseMarketMetricsResult} from './useMarketMetrics';

/**
 * Market metrics for the filters currently applied in the sidebar. Reuses the
 * statistics_v2 endpoint the chart queries — with default view toggles the request
 * body is identical, so RTK Query serves both from a single network call.
 */
export function useCurrentFilterMetrics(priceReport: PriceReportType): UseMarketMetricsResult {
  const {filter, errors} = useFilter();

  // Mirrors the StatisticsPage guard: an explicitly empty server list means “nothing selected”.
  const hasServersSelected = isNil(filter?.server) || !isEmpty(filter.server);
  const canFetch = isNotNil(filter) && hasServersSelected && isNil(errors);

  const queryArg = useMemo(
    () =>
      canFetch
        ? {
            reportId: priceReport.id,
            body: toMetricsRequestBody(filter, priceReport.sellingPrice.recommended.value ?? null),
          }
        : skipToken,
    [canFetch, filter, priceReport]
  );

  return useMarketMetrics(queryArg, priceReport);
}
